<template>
  <div>
    <WdsStack v-if="hireLoading" align-start direction-column gap-sm class="tw-w-full">
      <LoadingList :count="2" />
    </WdsStack>

    <WdsStack
      v-else-if="hasMilestones"
      direction-column
      class="tw-rounded tw-border tw-border-gray-200 tw-pb-3"
    >
      <WdsStack
        justify-space-between
        align-center
        class="tw-bg-true-gray-50 tw-border-b tw-border-gray-200 tw-px-4 tw-py-3"
      >
        <WdsStack gap-sm align-center class="tw-flex-wrap lg:tw-flex-nowrap">
          <WdsHeading type="title3" class="tw-pl-2">
            {{ $t('Milestones') }}
          </WdsHeading>
          <WdsLabel
            v-if="hasConfirmedSowStatements && hasCompanyCapabilities"
            xs
            subtle
            success
            class="tw-cursor-pointer"
            @click="openSowConfirmationModal"
          >
            <WdsStack gap-xs align-center class="tw-items-center">
              {{ $t('SoW status confirmed') }}
              <WdsTooltip
                :text="$t('This hire has been confirmed as a Statement of Work engagement.')"
              >
                <WdsIcon :size="14" name="help" color="#1e6021" />
              </WdsTooltip>
            </WdsStack>
          </WdsLabel>
        </WdsStack>
        <WdsButtonGhost
          v-if="hasCompanyCapabilities"
          secondary
          class="tw-text-primary tw-font-medium"
          @click="openMilestonesModal"
        >
          <WdsStack align-center gap-sm>
            {{ $t('Manage') }}
            <WdsIcon :size="16" name="chevron-right" />
          </WdsStack>
        </WdsButtonGhost>
        <WdsButtonGhost
          v-if="isWorker"
          primary
          :href="requestPaymentUrl"
          class="request-payment-btn"
        >
          <WdsStack align-center gap-sm>
            <WdsIcon :size="16" name="receipt" />
            <span>{{ $t('Request payment') }}</span>
          </WdsStack>
        </WdsButtonGhost>
      </WdsStack>

      <div v-if="milestonesLoading" class="tw-flex tw-justify-center tw-py-4">
        <WdsSpinner :size="24" indeterminate />
      </div>

      <div v-else-if="milestones.length > 0" class="tw-px-4 tw-pt-4">
        <MilestonesBlock
          v-model="milestones"
          :milestone-block-state="MilestoneEditState.display"
          :currency="currency"
        />
      </div>

      <div v-else class="tw-px-6 tw-py-6">
        <WdsText secondary>
          {{ $t('No milestones have been added to this hire.') }}
        </WdsText>
      </div>
    </WdsStack>
  </div>

  <MilestonesModal v-slot="{ open }" :hire-id="hireId">
    <div :ref="() => setMilestonesModalOpenFn(open)" style="display: none"></div>
  </MilestonesModal>

  <SowConfirmationModal v-model:open="showSowConfirmationModal" :hire-id="hireId" hide-footer />
</template>

<script setup lang="ts">
import {
  WdsText,
  WdsIcon,
  WdsStack,
  WdsButtonGhost,
  WdsHeading,
  WdsLabel,
  WdsTooltip,
  WdsSpinner,
} from '@worksome/wds-components'
import { useQuery } from '@vue/apollo-composable'
import { computed, ref } from 'vue'
import { graphql, useFragment } from '@/gql'
import { $t } from '@/composables/translation'
import LoadingList from '@/components/Dashboard/Widgets/Utils/LoadingList.vue'
import MilestonesModal from '@/components/Milestones/MilestonesModal.vue'
import MilestonesBlock from '@/components/Milestones/MilestonesBlock.vue'
import SowConfirmationModal from '@/components/Hire/HirePageCompliance/modals/SowConfirmationModal.vue'
import { MilestoneFragment } from '@/components/Milestones/fragments'
import { MilestoneEditState } from '@/components/Milestones/types'
import { ComplianceName } from '@/gql/graphql'
import { useAccountCapabilities } from '@/composables/useAccountCapabilities'
import { laravelNamedRoute } from '@/utils/laravelNamedRoutes'

const props = defineProps<{ hireId: string }>()

const { hasCompanyCapabilities, isWorker } = useAccountCapabilities()

const requestPaymentUrl = computed(() => laravelNamedRoute('bills.create', { hire: props.hireId }))

const HireMilestonesFragment = graphql(`
  fragment HireMilestonesType on Hire {
    id
    currency
    hasMilestones
  }
`)

const { result: hireResult, loading: hireLoading } = useQuery(
  graphql(`
    query HireMilestonesData($id: ID!) {
      hire(id: $id) {
        ...HireMilestonesType
      }
    }
  `),
  {
    id: props.hireId,
  },
  {
    clientId: 'publicApi',
  },
)

const { result: milestonesResult, loading: milestonesLoading } = useQuery(
  graphql(`
    query HireMilestonesList($accountId: ID!, $hireId: ID!) {
      milestones(accounts: [$accountId], hires: [$hireId]) {
        data {
          ...MilestoneType
        }
      }
    }
  `),
  () => ({
    accountId: currentAccount.globalId,
    hireId: props.hireId,
  }),
  {
    clientId: 'publicApi',
  },
)

const { result: complianceResult } = useQuery(
  graphql(`
    query HireMilestonesSoWCompliance($id: ID!, $names: [ComplianceName!]!) {
      compliance(id: $id, names: $names) {
        name
        completed
        applicable
      }
    }
  `),
  {
    id: props.hireId,
    names: [ComplianceName.SoWConfirmation],
  },
  {
    clientId: 'publicApi',
  },
)

const hire = computed(() => useFragment(HireMilestonesFragment, hireResult.value?.hire))

const hasMilestones = computed(() => hire.value?.hasMilestones ?? false)

const milestones = computed(() => {
  if (!milestonesResult.value?.milestones?.data) return []
  return milestonesResult.value.milestones.data.map((m) => useFragment(MilestoneFragment, m))
})

const sowConfirmationCompliance = computed(() => {
  const compliance = complianceResult.value?.compliance?.[0]
  return compliance?.applicable ? compliance : null
})

const hasConfirmedSowStatements = computed(
  () => sowConfirmationCompliance.value?.completed ?? false,
)

const currency = computed(() => hire.value?.currency ?? currentAccount.currency)

const milestonesModalOpenFn = ref<(() => void) | null>(null)
const showSowConfirmationModal = ref(false)

const setMilestonesModalOpenFn = (open: () => void) => {
  milestonesModalOpenFn.value = open
}

const openMilestonesModal = () => {
  if (milestonesModalOpenFn.value) {
    milestonesModalOpenFn.value()
  }
}

const openSowConfirmationModal = () => {
  showSowConfirmationModal.value = true
}
</script>

<style scoped>
.request-payment-btn {
  @apply tw-bg-true-gray-300;
  @apply tw-px-3.5;
  @apply tw-py-1.5;
}
</style>
